import useCheckPC from "@repo/hooks/utils/useCheckPC";
import classNames from "classnames";
import type { InputHTMLAttributes } from "react";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

import Button from "@components/atoms/Button";
import Icon from "@components/atoms/Icon";

import { IconName } from "@const/enum";

type TInputVariant = "underline" | "white" | "grey";
type TInputType = "text" | "number" | "email" | "password" | "search" | "tel";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  type?: TInputType;
  variant?: TInputVariant;
  className?: string;
  hasError?: boolean;
  leftIconNode?: React.ReactNode;
  rightIconNode?: React.ReactNode;
  showDelete?: boolean;
  onClickDelete?: () => void;
  hasInputValue?: boolean;
  setHasInputValue?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Input = forwardRef<HTMLInputElement, IProps>(
  (
    {
      type = "text",
      variant = "underline",
      value,
      className,
      hasError = false,
      leftIconNode,
      rightIconNode,
      showDelete = false,
      onClickDelete,
      hasInputValue = true,
      setHasInputValue,
      ...props
    }: IProps,
    outerRef,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(outerRef, () => innerRef.current!, []);

    const { isPC } = useCheckPC();

    const [isFocused, setIsFocused] = useState(false);

    return (
      <div
        className={classNames(
          "flex w-full items-center disabled:text-tertiary",
          variant === "underline" && {
            "h-6 border-b border-solid border-black bg-transparent px-0 pb-1 pt-0": true,
          },
          variant === "white" && {
            "h-9 rounded-sm bg-btn-tertiary px-3 py-2": true,
          },
          variant === "grey" && {
            "gap-2 rounded-md bg-tertiary px-3 text-black": true,
            "h-7.5 py-1.25": isPC,
            "h-9 py-2": !isPC,
          },
          hasError && "border border-red-400",
          className,
        )}
      >
        {Boolean(leftIconNode) && (
          <div className={classNames(variant === "grey" && { "text-black": isFocused })}>{leftIconNode}</div>
        )}
        <input
          className={classNames(
            "h-5 w-full bg-inherit font-regular placeholder:align-middle placeholder:text-secondary disabled:text-tertiary",
            className,
          )}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (["number", "tel"].includes(type)) {
              const target = e.currentTarget;
              const hasInvalidKey = /[^0-9]/g.test(target.value);

              if (hasInvalidKey) {
                const selectionStart = target.selectionStart;
                if (selectionStart !== null) {
                  target.value = target.value.replace(/[^0-9]*/g, "");
                  target.setSelectionRange(selectionStart - 1, selectionStart - 1);
                  e.preventDefault();
                }
              }
            }
            props.onInput?.(e);
            showDelete && setHasInputValue?.(Boolean(e.target.value));
          }}
          ref={innerRef}
          type={type}
          value={value}
          {...props}
        />
        {(Boolean(rightIconNode) || showDelete) && hasInputValue ? (
          <div className={classNames(variant === "grey" && { "text-primary": isFocused })}>
            {rightIconNode}
            {showDelete ? (
              <Button
                onClick={() => {
                  if (innerRef.current) innerRef.current.value = "";
                  setHasInputValue?.(false);
                  onClickDelete?.();
                }}
                variant="icon"
              >
                <Icon name={IconName.Close_100} />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
